import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import { useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import ModalSide from "src/components/Modal/styles";
import { IRefSchedule } from "src/components/ScheduleRestriction/interface";
import Tabs from "src/components/Tabs";
import { useAuth } from "src/context/Auth/context";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import CompaniesUser from "./components/CompaniesUser";
import GroupsUser from "./components/GroupsUser";
import InviteUser from "./components/InviteUser";
import RegisterUser from "./components/RegisterUser";
import { IRefRegisterUser } from "./components/RegisterUser/interface";
import Restrictions from "./components/Restrictions";
import { IComponentUsersModals, IRefUsersModals, IUserMaintenance } from "./interface";
import invitesSchema, { maintenanceSchema, registerSchema } from "./schemas";

const ModalsUsers: React.ForwardRefRenderFunction<IRefUsersModals, IComponentUsersModals> = ({
	paginationRef,
	paginationInvitationRef
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const { businessGroup } = useAuth();
	const { handleSubmit, control, register, clearErrors, formState: {errors}, reset, setError } = useForm();
	const { confirm } = useConfirmModal();

	const modalInvite = useRef<RCCRefModalProps>(null);
	const businessGroupId = useRef(businessGroup.efa001Id);

	const submit = async (data: any) => {
		try{
			clearErrors();
			if(!data.invites || data.invites.length === 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos um convite"),
					title: translate("Atenção")
				});
				return;
			}
			modalInvite.current?.setLoadingButton();

			const invites = data.invites.map((invite: any) => {
				if(typeof invite.efa026Efa004Id === "object")
					return {
						...invite,
						efa026Efa004Id: invite.efa026Efa004Id.value
					};
				return invite;
			});

			await invitesSchema(translate).validate({invites}, {
				abortEarly: false
			});

			await RCCServicesApi.postRequest({
				method: "User/Invite",
				body: {
					businessGroupId: businessGroupId.current ?? null,
					invites
				}
			});

			addToast({
				color: "green",
				description: translate("Convites enviados com sucesso"),
				title: translate("Sucesso")
			});

			modalInvite.current?.resetLoadingButton();
			modalInvite.current?.closeModal();
			paginationInvitationRef?.current?.reload();
		}catch(err){
			modalInvite.current?.resetLoadingButton();
			if(getValidationErrors(err, setError))
				return;

			standardErrorMessages(err, translate("Erro ao enviar convites"), addToast);
		}	


	};

	const actionButtonDelete = (id: number, name: string) => {
		async function deleteMethod(){
			await RCCServicesApi.deleteRequest({
				method: "User/DeleteFromBusinessGroup",
				urlParameters: {
					id
				}
			});
		}
    
		confirm(
			translate("Exclusão de usuário"), 
			`${translate("Deseja realmente excluir o usuário")} (${name}) ${translate("desse grupo empresa")}?`,
			async () => {
				await deleteMethod();
				addToast({
					title: translate("Sucesso"),
					description: translate("Usuário excluído com sucesso do grupo empresa"),
					color: "green"
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao excluir usuário do grupo empresa"), addToast);
			},
			true
		);
	};

	const actionButtonDisable = (id: number, name: string) => {
		async function disableMethod(){
			await RCCServicesApi.putRequest({
				method: "User/DisableFromBusinessGroup",
				urlParameters: {
					id
				}
			});
		}
    
		confirm(
			translate("Desabilitar o usuário"), 
			`${translate("Deseja realmente desabilitar o usuário")} (${name}) ${translate("desse grupo empresa")}?`,
			async () => {
				await disableMethod();
				addToast({
					title: translate("Sucesso"),
					description: translate("Usuário desabilitado com sucesso do grupo empresa"),
					color: "green"
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao desabilitar usuário do grupo empresa"), addToast);
			},
			true
		);
	};

	const actionButtonRestore = (id: number, name: string) => {
		async function putMethod(){
			await RCCServicesApi.putRequest({
				method: "User/RestoreFromBusinessGroup",
				urlParameters: {
					id
				}
			});
		}
    
		confirm(
			translate("Restaurar acesso à empresa"), 
			`${translate("Deseja realmente restaurar o acesso do usuário")} (${name}) ${translate("desse grupo empresa")}?`,
			async () => {
				await putMethod();
				addToast({
					title: translate("Sucesso"),
					description: translate("Usuário restaurado com sucesso do grupo empresa"),
					color: "green"
				});
				paginationRef.current?.reload();
			},
			(err) => {
				standardErrorMessages(err, translate("Erro ao restaurar usuário do grupo empresa"), addToast);
			},
			true
		);
	};

	const {
		handleSubmit: handleSubmitMaintenance, 
		control: controlMaintenance, 
		register: registerMaintenance,
		clearErrors: clearErrorsMaintenance, 
		formState: {errors: errorsMaintenance}, 
		reset: resetMaintenance, 
		setError: setErrorMaintenance
	} = useForm();

	const [subtitle, setSubtitle] = useState<string>("");
	const [userMaintenance, setUserMaintenance] = useState<IUserMaintenance>({} as IUserMaintenance);

	const modalMaintenance = useRef<RCCRefModalProps>(null);
	const userIdMaintenance = useRef<number>(0);

	const refSchedule = useRef<IRefSchedule>(null);

	const actionButtonMaintenance = async (id: number, subtitleModal: string) => {
		try{
			resetMaintenance();
			setLoading(true);
			const response = await RCCServicesApi.getRequest({
				method: "User/Maintenance",
				urlParameters: {
					id
				}
			});
			userIdMaintenance.current = id;
			setUserMaintenance(response.data as IUserMaintenance);
			setSubtitle(subtitleModal);
			setLoading(false);
			modalMaintenance.current?.openModal();
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao buscar informações para manutenção do usuário"), addToast);
		}
	};

	const onSubmitMaintenance = async (dt: any) => {
		try{
			clearErrorsMaintenance();
			if(!dt.eftba004 || dt.eftba004.length === 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos um grupo de usuário"),
					title: translate("Atenção")
				});
				return;
			}

			const dataChanged = dt;

			dataChanged.eftba004 = dt?.eftba004?.filter((efa004: any) => efa004.efa004Id &&  efa004.efa004Id !== "0");
			dataChanged.eftba002 = dt?.eftba002?.filter((efa002: any) => efa002.efa002Id &&  efa002.efa002Id !== "0");

			modalMaintenance.current?.setLoadingButton();

			await maintenanceSchema(translate).validate({eftba004: dataChanged.eftba004, eftba002: dataChanged.eftba002}, {
				abortEarly: false
			});

			const schedule = refSchedule.current?.getInnerData() ?? [];

			await RCCServicesApi.putRequest({
				method: "User/ExecuteMaintenance",
				body: {
					efa005Id: userIdMaintenance.current,
					eftba004Ids: dataChanged.eftba004.map((efa004: any) => Number(efa004.efa004Id)),
					validateIP: dataChanged.validateIP ? "S" : "N",
					eftba002Ids: dataChanged.eftba002.map((efa002: any) => Number(efa002.efa002Id)),
					schedule: schedule.map((sched: any[]) => sched.map((s) => s.toString()))
				}
			});

			addToast({
				color: "green",
				description: translate("Manutenção do usuário efetuada com sucesso"),
				title: translate("Sucesso")
			});

			modalMaintenance.current?.resetLoadingButton();
			modalMaintenance.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err){
			modalMaintenance.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorMaintenance))
				return;

			standardErrorMessages(err, translate("Erro ao efetuar manutenção do usuário"), addToast);
		}	
	};


	// Modal de registro de usuário
	const modalRegister = useRef<RCCRefModalProps>(null);
	const refRegisterUser = useRef<IRefRegisterUser>(null);

	const {
		handleSubmit: handleSubmitRegister, 
		control: controlRegister, 
		register: registerRegister,
		clearErrors: clearErrorsRegister, 
		formState: {errors: errorsRegister}, 
		setValue: setValueRegister,
		reset: resetRegister, 
		setError: setErrorRegister
	} = useForm();

	const onSubmitInsertUser = async (dt: any) => {
		clearErrorsRegister();
		try{
			if(!dt.eftba004 || dt.eftba004.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos um grupo de usuário"),
					title: translate("Atenção")
				});
				return;
			}

			if(!dt.eftba002 || dt.eftba002.length <= 0){
				addToast({
					color: "yellow",
					description: translate("Insira ao menos uma empresa"),
					title: translate("Atenção")
				});
				return;
			}

			const dataChanged = dt;

			dataChanged.eftba004 = dt?.eftba004?.filter((efa004: any) => efa004.efa004Id &&  efa004.efa004Id !== "0");
			dataChanged.eftba002 = dt?.eftba002?.filter((efa002: any) => efa002.efa002Id &&  efa002.efa002Id !== "0");

			modalRegister.current?.setLoadingButton();

			const schedule = refRegisterUser?.current?.getScheduleData();

			const dataToInsert = {
				...dataChanged,
				efa005Paginicial: dataChanged.efa005Paginicial ? Number(dataChanged.efa005Paginicial): null,
				efa005Id: userIdMaintenance.current,
				eftba004: dataChanged.eftba004?.map((efa004: any) => Number(efa004.efa004Id)),
				eftba002: dataChanged.eftba002?.map((efa002: any) => Number(efa002.efa002Id)),
				schedule: schedule?.map((sched: any[]) => sched.map((s) => s.toString())),
				validateIP: dataChanged.validateIP ? "S" : "N",
				efa001Id: businessGroupId.current,
				daysExpirePassword: dataChanged.daysExpirePassword ? Number(dataChanged.daysExpirePassword): null
			};

			await registerSchema(translate).validate(dataToInsert, 
				{abortEarly: false}
			);

			await RCCServicesApi.postRequest({
				method: "User/InsertUser",
				body: dataToInsert
			});

			addToast({
				color: "green",
				description: translate("Usuário registrado com sucesso"),
				title: translate("Sucesso")
			});

			modalRegister.current?.resetLoadingButton();
			modalRegister.current?.closeModal();
			paginationRef.current?.reload();
		}catch(err: any){
			modalRegister.current?.resetLoadingButton();
			if(getValidationErrors(err, setErrorRegister)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			standardErrorMessages(err, translate("Erro ao registrar usuário"), addToast);
		}	
	};

	useImperativeHandle(ref,() => ({
		actionButtonInvite: (bGroupId?: number) => {
			reset();
			if(bGroupId)
				businessGroupId.current = bGroupId;
			modalInvite.current?.openModal();
		},
		actionButtonRegister: (bGroupId: number) => {
			resetRegister();
			businessGroupId.current = bGroupId;
			modalRegister.current?.openModal();
		},
		actionButtonDelete,
		actionButtonMaintenance,
		actionButtonRestore,
		actionButtonDisable
	}));

	return <>
	
		{/* Modal de de convidar usuário */}
		<Modal
			ref={modalInvite}
			header={{
				title: translate("Convidar usuários"),
				icon: {
					name: "users"
				}
			}}
			onSubmit={handleSubmit(submit)}
			styleInline={ModalSide}
		>
			<InviteUser 
				clearErrors={clearErrors}
				control={control}
				error={errors}
				register={register}
				businessGroupId={businessGroupId.current}
			/>
		</Modal>

		{/* Modal de manutenção de usuario */}
		<Modal
			ref={modalMaintenance}
			header={{
				title: translate("Manutenção"),
				subtitle,
				icon: {
					name: "settings"
				}
			}}
			onSubmit={handleSubmitMaintenance(onSubmitMaintenance)}
			styleInline={ModalSide}
		>
			<Tabs 
				name="tabUserMaintenance"
				tabInit={0}
				options={[{
					label: translate("Grupo de usuários"),
					content: <GroupsUser 
						clearErrors={clearErrorsMaintenance}
						control={controlMaintenance}
						defaultValue={userMaintenance.eftba004}
						error={errorsMaintenance}
					/>
				},
				{
					label: translate("Empresas"),
					content: <CompaniesUser
						clearErrors={clearErrorsMaintenance}
						control={controlMaintenance}
						defaultValue={userMaintenance.eftba002}
						error={errorsMaintenance}
					/>
				},
				{
					label: translate("Restrições"),
					content: <Restrictions 
					 	register={registerMaintenance} 
					 	refSchedule={refSchedule} 
					 	data={userMaintenance}  
					 />
				}
				]}
			/>
		</Modal>

		{/* Modal de registro de usuario */}
		<Modal
			ref={modalRegister}
			header={{
				title: translate("Adicionar usuário"),
				icon: {
					name: "user"
				}
			}}
			onSubmit={handleSubmitRegister(onSubmitInsertUser)}
			styleInline={ModalSide}
		>
			<RegisterUser 
				ref={refRegisterUser}
				clearErrors={clearErrorsRegister}
				control={controlRegister}
				setValue={setValueRegister}
				error={errorsRegister}
				register={registerRegister}
				businessGroupId={businessGroupId.current}
			/>
		</Modal>

	</>;
};

export default forwardRef(ModalsUsers);
